// Tenantra Inc. - Rent wallet setup (after identity verification): business, owners 25%+, signer ID, payout bank
import { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Plus, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { navigate } from '@/services/navigation';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pill } from '@/components/dashboard/ScreeningAddresses';

const PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];
const STEPS = [['submitted', 'Submitted'], ['under_review', 'Under review'], ['active', 'Active']];

const L = ({ label, children, className = '' }) => (
  <label className={`block text-xs text-slate-600 ${className}`}>{label}<div className="mt-1">{children}</div></label>
);

function FileField({ label, value, onChange, testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const { data } = await api.post('/files/upload?file_type=document', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange({ id: data.file_id, name: f.name });
    } catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between gap-3 border border-slate-200 rounded-md px-3 h-10" data-testid={testId}>
      <span className="text-sm text-slate-700 truncate">{value ? <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" />{value.name}</span> : label}</span>
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid={`${testId}-input`} />
      <button type="button" onClick={() => ref.current.click()} className="text-xs text-teal-700 inline-flex items-center gap-1 flex-shrink-0" data-testid={`${testId}-btn`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}{value ? 'Replace' : 'Upload'}
      </button>
    </div>
  );
}

function DobPicker({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const d = value ? new Date(`${value}T12:00:00`) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="w-full h-10 px-3 text-left text-sm border border-slate-200 rounded-md bg-white" data-testid={testId}>{d ? d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : <span className="text-slate-400">Select</span>}</button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white" align="start">
        <Calendar mode="single" selected={d} defaultMonth={d || new Date(1980, 0)} captionLayout="dropdown-buttons" fromYear={1920} toYear={new Date().getFullYear() - 18}
          onSelect={(x) => { if (x) { onChange(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`); setOpen(false); } }} initialFocus />
      </PopoverContent>
    </Popover>
  );
}

function Status({ w }) {
  const idx = STEPS.findIndex(([k]) => k === w.status);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="wallet-status-card">
      <div className="flex items-center justify-between"><p className="text-sm font-medium text-slate-800">{w.legal_name}</p>{w.status === 'rejected' ? <Pill tone="red">Rejected</Pill> : null}</div>
      <ol className="flex items-center gap-2">
        {STEPS.map(([k, l], i) => (
          <li key={k} className="flex items-center gap-2 flex-1" data-testid={`wallet-step-${k}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${i <= idx ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-400'}`}>{i < idx || w.status === 'active' ? <Check className="w-3 h-3" /> : i + 1}</span>
            <span className={`text-xs ${i <= idx ? 'text-slate-800' : 'text-slate-400'}`}>{l}</span>
            {i < STEPS.length - 1 && <span className={`flex-1 h-px ${i < idx ? 'bg-teal-600' : 'bg-slate-200'}`} />}
          </li>
        ))}
      </ol>
      <p className="text-xs text-slate-500">Payout {w.payout_institution} ••••{w.payout_last4}</p>
      {w.note && <p className="text-xs text-slate-600" data-testid="wallet-note">{w.note}</p>}
      <button onClick={() => navigate('/landlord/payments')} className="h-9 px-4 rounded-md bg-teal-600 hover:bg-teal-700 transition-colors text-white text-sm font-medium" data-testid="wallet-go-payments">Go to Payments</button>
    </div>
  );
}

export default function WalletSetupPage() {
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ entity_type: 'individual', province: 'ON', owners: [] });
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => api.get('/landlord/wallet').then(r => {
    setInfo(r.data);
    const p = r.data.prefill || {};
    setF(x => ({ ...x, legal_name: p.legal_name || '', signer_name: p.signer_name || '', street: p.street || '', city: p.city || '', province: p.province || 'ON', postal_code: p.postal_code || '' }));
  }).catch(e => toast.error(apiHelpers.handleError(e)));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setOwner = (i, k, v) => setF({ ...f, owners: f.owners.map((o, j) => (j === i ? { ...o, [k]: v } : o)) });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/landlord/wallet/application', {
        ...f, owners: f.owners.map(o => ({ ...o, pct: Number(o.pct) })), business_number: f.business_number || null,
        signer_id_file_id: files.id?.id, registration_file_id: files.reg?.id || null, void_cheque_file_id: files.cheque?.id,
      });
      toast.success('Submitted'); load();
    } catch (err) { toast.error(apiHelpers.handleError(err)); } finally { setBusy(false); }
  };
  if (!info) return <div className="h-40 bg-slate-100 rounded-lg animate-pulse" />;
  const w = info.wallet;
  const inp = 'h-10 bg-white';
  return (
    <div className="max-w-3xl space-y-4" data-testid="wallet-setup-page">
      <h1 className="text-[15px] font-medium text-slate-800">Rent wallet</h1>
      {!info.eligible && !w ? (
        <div className="bg-white border border-slate-200 rounded-lg p-5 flex flex-wrap items-center justify-between gap-3" data-testid="wallet-locked">
          <p className="text-sm text-slate-700">Rent collection is included with a subscription</p>
          <button onClick={() => navigate('/landlord/billing')} className="h-9 px-4 rounded-md bg-teal-600 hover:bg-teal-700 transition-colors text-white text-sm font-medium" data-testid="wallet-choose-plan">Choose a plan</button>
        </div>
      ) : !info.verified && !w ? (
        <div className="bg-white border border-slate-200 rounded-lg p-5 flex flex-wrap items-center justify-between gap-3" data-testid="wallet-needs-verification">
          <p className="text-sm text-slate-700">Verify your identity first</p>
          <button onClick={() => navigate('/landlord/verify')} className="h-9 px-4 rounded-md bg-teal-600 text-white text-sm font-medium" data-testid="wallet-verify-btn">Verify</button>
        </div>
      ) : w && w.status !== 'rejected' ? <Status w={w} /> : (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="wallet-form">
          {w?.status === 'rejected' && <div className="px-5 py-3"><Status w={w} /></div>}
          <div className="p-5 grid sm:grid-cols-2 gap-4">
            <p className="sm:col-span-2 text-sm font-medium text-slate-800">Business</p>
            <L label="Legal name"><Input required value={f.legal_name} onChange={set('legal_name')} className={inp} data-testid="wallet-legal-name" /></L>
            <L label="Type"><Select value={f.entity_type} onValueChange={(v) => setF({ ...f, entity_type: v })}>
              <SelectTrigger className="h-10 bg-white" data-testid="wallet-entity-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{[['individual', 'Individual landlord'], ['corporation', 'Corporation'], ['partnership', 'Partnership'], ['non_profit', 'Non-profit']].map(([k, l]) => <SelectItem key={k} value={k} data-testid={`wallet-entity-${k}`}>{l}</SelectItem>)}</SelectContent></Select></L>
            {f.entity_type !== 'individual' && <>
              <L label="Business number"><Input value={f.business_number || ''} onChange={set('business_number')} className={inp} data-testid="wallet-business-number" /></L>
              <L label="Business registration"><FileField label="Articles or registration" value={files.reg} onChange={(v) => setFiles({ ...files, reg: v })} testId="wallet-file-registration" /></L>
            </>}
            <L label="Street" className="sm:col-span-2"><Input required value={f.street} onChange={set('street')} className={inp} data-testid="wallet-street" /></L>
            <L label="City"><Input required value={f.city} onChange={set('city')} className={inp} data-testid="wallet-city" /></L>
            <div className="grid grid-cols-2 gap-3">
              <L label="Province"><Select value={f.province} onValueChange={(v) => setF({ ...f, province: v })}>
                <SelectTrigger className="h-10 bg-white" data-testid="wallet-province"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{PROVINCES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></L>
              <L label="Postal code"><Input required value={f.postal_code} onChange={set('postal_code')} className={inp} data-testid="wallet-postal" /></L>
            </div>
          </div>
          {f.entity_type !== 'individual' && (
            <div className="p-5 space-y-3" data-testid="wallet-owners">
              <div className="flex items-center justify-between"><p className="text-sm font-medium text-slate-800">Owners 25%+</p>
                {f.owners.length < 4 && <button type="button" onClick={() => setF({ ...f, owners: [...f.owners, { name: '', pct: '', dob: '' }] })} className="text-xs text-teal-700 inline-flex items-center gap-1" data-testid="wallet-add-owner"><Plus className="w-3.5 h-3.5" /> Add</button>}</div>
              {f.owners.map((o, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`wallet-owner-${i}`}>
                  <L label="Name" className="col-span-5"><Input required value={o.name} onChange={(e) => setOwner(i, 'name', e.target.value)} className={inp} /></L>
                  <L label="%" className="col-span-2"><Input required inputMode="decimal" value={o.pct} onChange={(e) => setOwner(i, 'pct', e.target.value.replace(/[^\d.]/g, ''))} className={inp} /></L>
                  <L label="Date of birth" className="col-span-4"><DobPicker value={o.dob} onChange={(v) => setOwner(i, 'dob', v)} testId={`wallet-owner-dob-${i}`} /></L>
                  <button type="button" onClick={() => setF({ ...f, owners: f.owners.filter((_, j) => j !== i) })} className="col-span-1 h-10 flex items-center justify-center text-slate-400 hover:text-red-600" aria-label="Remove owner"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="p-5 grid sm:grid-cols-2 gap-4">
            <p className="sm:col-span-2 text-sm font-medium text-slate-800">Signer</p>
            <L label="Full name"><Input required value={f.signer_name} onChange={set('signer_name')} className={inp} data-testid="wallet-signer-name" /></L>
            <L label="Title"><Input required value={f.signer_title || ''} onChange={set('signer_title')} placeholder="Owner" className={inp} data-testid="wallet-signer-title" /></L>
            <L label="Government ID" className="sm:col-span-2"><FileField label="Driver's licence or passport" value={files.id} onChange={(v) => setFiles({ ...files, id: v })} testId="wallet-file-id" /></L>
          </div>
          <div className="p-5 grid sm:grid-cols-3 gap-4">
            <p className="sm:col-span-3 text-sm font-medium text-slate-800">Payout bank account</p>
            <L label="Institution"><Input required inputMode="numeric" maxLength={3} value={f.institution_number || ''} onChange={(e) => setF({ ...f, institution_number: e.target.value.replace(/\D/g, '') })} className={inp} data-testid="wallet-institution" /></L>
            <L label="Transit"><Input required inputMode="numeric" maxLength={5} value={f.transit_number || ''} onChange={(e) => setF({ ...f, transit_number: e.target.value.replace(/\D/g, '') })} className={inp} data-testid="wallet-transit" /></L>
            <L label="Account"><Input required inputMode="numeric" maxLength={12} value={f.account_number || ''} onChange={(e) => setF({ ...f, account_number: e.target.value.replace(/\D/g, '') })} className={inp} data-testid="wallet-account" /></L>
            <L label="Void cheque" className="sm:col-span-3"><FileField label="Void cheque or bank letter" value={files.cheque} onChange={(v) => setFiles({ ...files, cheque: v })} testId="wallet-file-cheque" /></L>
          </div>
          <div className="p-5 flex justify-end">
            <button type="submit" disabled={busy || !files.id || !files.cheque || (f.entity_type !== 'individual' && !files.reg)} className="h-10 px-5 rounded-md bg-teal-600 hover:bg-teal-700 transition-colors text-white text-sm font-medium disabled:opacity-40 inline-flex items-center gap-2" data-testid="wallet-submit">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
